import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  EmailSearchResult,
  GetEmailSearchResultsResponse,
} from '@dreamai/types';
import { useAuth0 } from '@auth0/auth0-react';
import api from 'api';
import { useDependencyUpdates } from './DependencyUpdateProvider';

interface EmailListState {
  emails: EmailSearchResult[];
  query: string;
  loading: boolean;
  error: string | null;
  lastUpdated: number;
}

interface EmailListContextType {
  getEmailList: (emailListId: string) => EmailListState;
  refreshEmailList: (emailListId: string) => Promise<void>;
  isLoading: (emailListId: string) => boolean;
}

const EmailListContext = createContext<EmailListContextType | null>(null);

interface EmailListProviderProps {
  children: React.ReactNode;
}

export const EmailListProvider: React.FC<EmailListProviderProps> = ({
  children,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { registerCallback } = useDependencyUpdates();
  const [emailLists, setEmailLists] = useState<Map<string, EmailListState>>(
    new Map(),
  );
  const unregisterCallbacksRef = useRef<Map<string, () => void>>(new Map());

  const getDefaultState = (): EmailListState => ({
    emails: [],
    query: '',
    loading: true,
    error: null,
    lastUpdated: 0,
  });

  const fetchEmailList = useCallback(
    async (emailListId: string): Promise<EmailListState> => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<GetEmailSearchResultsResponse>(
          `/agent-email-storage/search-results/${emailListId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        return {
          emails: response.data.results || [],
          query: response.data.query || '',
          loading: false,
          error: null,
          lastUpdated: Date.now(),
        };
      } catch (error) {
        console.error('Error fetching email list:', error);
        return {
          emails: [],
          query: '',
          loading: false,
          error: 'Failed to load email list',
          lastUpdated: Date.now(),
        };
      }
    },
    [getAccessTokenSilently],
  );

  const smartMergeEmails = (
    currentEmails: EmailSearchResult[],
    newEmails: EmailSearchResult[],
  ): EmailSearchResult[] => {
    // Create a map of current emails by ID for quick lookup
    const currentEmailMap = new Map(
      currentEmails.map((email) => [email.id, email]),
    );

    // Start with new emails as the base (this maintains the correct order from the server)
    const mergedEmails = [...newEmails];

    // For each new email, if we had a previous version, preserve any client-side state
    // that might have been added (like selection state, etc.)
    return mergedEmails.map((newEmail) => {
      const currentEmail = currentEmailMap.get(newEmail.id);
      if (currentEmail) {
        // Merge server data with any client-side state
        // In this case, we prioritize server data but could preserve client state if needed
        return {
          ...currentEmail,
          ...newEmail,
          // Preserve any client-side only properties here if needed
        };
      }
      return newEmail;
    });
  };

  const refreshEmailList = useCallback(
    async (emailListId: string) => {
      setEmailLists((prev) => {
        const current = prev.get(emailListId) || getDefaultState();
        const updated = new Map(prev);
        updated.set(emailListId, {
          ...current,
          loading: true,
          error: null,
        });
        return updated;
      });

      const newState = await fetchEmailList(emailListId);

      setEmailLists((prev) => {
        const current = prev.get(emailListId) || getDefaultState();
        const updated = new Map(prev);

        // Smart merge: preserve order from server but merge with existing data
        const mergedEmails = smartMergeEmails(current.emails, newState.emails);

        updated.set(emailListId, {
          ...newState,
          emails: mergedEmails,
        });
        return updated;
      });
    },
    [fetchEmailList],
  );

  const registerForUpdates = useCallback(
    (emailListId: string) => {
      // Unregister any existing callback for this email list
      const existingUnregister =
        unregisterCallbacksRef.current.get(emailListId);
      if (existingUnregister) {
        existingUnregister();
      }

      // Register for dependency updates on this specific email list record
      const unregister = registerCallback(
        'agent_storage_email_list',
        emailListId,
        (update) => {
          console.log(`Email list ${emailListId} was updated, refreshing...`);
          refreshEmailList(emailListId);
        },
      );

      unregisterCallbacksRef.current.set(emailListId, unregister);
    },
    [registerCallback, refreshEmailList],
  );

  const getEmailList = useCallback(
    (emailListId: string): EmailListState => {
      const existing = emailLists.get(emailListId);

      if (!existing) {
        // Initialize the email list and start fetching
        const initialState = getDefaultState();
        setEmailLists((prev) => {
          const updated = new Map(prev);
          updated.set(emailListId, initialState);
          return updated;
        });

        // Start fetching and register for updates
        refreshEmailList(emailListId);
        registerForUpdates(emailListId);

        return initialState;
      }

      return existing;
    },
    [emailLists, refreshEmailList, registerForUpdates],
  );

  const isLoading = useCallback(
    (emailListId: string): boolean => {
      const state = emailLists.get(emailListId);
      return state?.loading ?? true;
    },
    [emailLists],
  );

  // Cleanup callbacks on unmount
  useEffect(() => {
    return () => {
      unregisterCallbacksRef.current.forEach((unregister) => unregister());
      unregisterCallbacksRef.current.clear();
    };
  }, []);

  const contextValue: EmailListContextType = {
    getEmailList,
    refreshEmailList,
    isLoading,
  };

  return (
    <EmailListContext.Provider value={contextValue}>
      {children}
    </EmailListContext.Provider>
  );
};

export const useEmailList = (): EmailListContextType => {
  const context = useContext(EmailListContext);
  if (!context) {
    throw new Error('useEmailList must be used within an EmailListProvider');
  }
  return context;
};
